import React, { useEffect, useRef } from "react";
import { useIntl } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";
import { Lock } from "@piiqtechnologies/svg-icons";
import { PeekBox, PeekButton, PeekStack, PeekTypography } from "@piiqtechnologies/ui-components";
import { buildOrgPath, PATHS } from "@constants/Paths";
import { AnalyticsArea, useTrackEvent } from "@hooks";
import { SeoHead } from "@components/common/seo-head/SeoHead";
import { useOptionalActiveOrganization } from "@stores/ActiveOrganizationContext";
import { useAuthContext } from "@stores/AuthContext";
import { getIsAdmin } from "@stores/utils";
import messages from "./messages.i18n";
export const Restricted = () => {
    var _a;
    const intl = useIntl();
    const navigate = useNavigate();
    const location = useLocation();
    const track = useTrackEvent();
    const { user } = useAuthContext();
    const isAdmin = getIsAdmin(user);
    const activeOrgContext = useOptionalActiveOrganization();
    // Two variants of the same screen, not two screens: the button appears when the resolver has
    // currently resolved an organization (never what it could resolve) or the caller is staff.
    const showBackToCommunities = Boolean(activeOrgContext === null || activeOrgContext === void 0 ? void 0 : activeOrgContext.activeOrg) || isAdmin;
    const { communityId, code } = (_a = location.state) !== null && _a !== void 0 ? _a : {};
    const hasTrackedRef = useRef(false);
    useEffect(() => {
        if (hasTrackedRef.current)
            return;
        hasTrackedRef.current = true;
        track(AnalyticsArea.Community, "Restricted Hit", { communityId, code });
    }, [track, communityId, code]);
    const handleBackToCommunities = () => {
        // Only rendered when showBackToCommunities is true, i.e. isAdmin or activeOrgContext is
        // resolved — so one of the two branches below always applies.
        if (isAdmin) {
            navigate(PATHS.ADMIN_DASHBOARD);
            return;
        }
        if (activeOrgContext) {
            navigate(buildOrgPath(activeOrgContext.activeOrgId, PATHS.HOME));
        }
    };
    return (React.createElement(PeekStack, { "data-testid": "restricted-screen", height: "100%", gap: 2, alignItems: "center", justifyContent: "center", px: 2, sx: { textAlign: "center" } },
        React.createElement(SeoHead, { title: intl.formatMessage(messages.seoTitle), description: intl.formatMessage(messages.seoDescription), canonicalPath: "/restricted" }),
        React.createElement(PeekBox, { sx: {
                width: 64,
                height: 64,
                borderRadius: "50%",
                backgroundColor: "grey.100",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
            } },
            React.createElement(PeekBox, { sx: { display: "inline-flex", color: "grey.500" } },
                React.createElement(Lock, { width: 26, height: 26 }))),
        React.createElement(PeekStack, { gap: 1, alignItems: "center" },
            React.createElement(PeekTypography, { variant: "h2" }, intl.formatMessage(messages.title)),
            React.createElement(PeekTypography, { variant: "body1", color: "grey.500" }, intl.formatMessage(messages.description))),
        showBackToCommunities && (React.createElement(PeekButton, { size: "large", variant: "contained", onClick: handleBackToCommunities }, intl.formatMessage(messages.backToCommunities)))));
};
export default Restricted;
